import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Select from "react-select";

import {
  getAccrualTypes,
  saveLaborRule,
  showNotificationWithTimeout,
} from "../../../Redux/actions";
import ErrorContainer from "../../Components/ErrorContainer";
import ModelContainer from "../../Components/ModelContainer";
import { capitalizeFirstLetter, checkNull } from "../../../helpers";

const schema = yup
  .object({
    id: yup.string(),
    rule_name: yup.string().required(),
    leave_type_id: yup.string().required(),
    employment_status_id: yup.string().required(),
    employee_function_id: yup.string().required(),
    employee_category_id: yup.string().required(),
    employee_category: yup.string().when("employee_category_id", {
      is: "1",
      then: yup.string().required(),
    }),
    employee_select: yup.array().required().min(1),
    allotment_amount: yup.number().positive().integer().required(),
    accrual_type_id: yup.string().required(),
    birthday_expired: yup.string().when("leave_type_id", {
      is: "7",
      then: yup.string().required(),
    }),
    expiry_months: yup.string().when("leave_type_id", {
      is: "9",
      then: yup.string().required(),
    }),
    accrual_threshold_id: yup.string().when("accrual_type_id", {
      is: (accrualTypeId) =>
        [
          3, 4, 7, 8, 11, 12, 13, 15, 16, 19, 20, 23, 24, 29, 30, 37, 38,
        ].includes(parseInt(accrualTypeId)),
      then: yup.string().required(),
    }),
    carryover_period_id: yup.string().when("leave_type_id", {
      is: (leaveTypeId) =>
        [1, 2, 3, 4, 5, 6, 8, 11, 14].includes(parseInt(leaveTypeId)),
      then: yup.string().required(),
    }),
    maximum_carryover_days: yup.number().when("carryover_period_id", {
      is: (carryoverPeriodId) =>
        carryoverPeriodId !== "" && carryoverPeriodId != 7,
      then: yup.number().positive().integer().required(),
    }),
    annual_refresh_cycle: yup.string().required(),
    additional_allotment_full_cycle: yup.string().required(),
    additional_allotment_full_cycle_days: yup
      .number()
      .when("additional_allotment_full_cycle", {
        is: "yes",
        then: yup.number().positive().integer().required(),
      }),
    max_allotment_amount: yup.number().when("additional_allotment_full_cycle", {
      is: "yes",
      then: yup.number().positive().required(),
    }),
  })
  .required();
const LeaveRule = ({ openDeleteRuleModal }) => {
  const dispatch = useDispatch();
  const {
    account,
    leaveTypes,
    employmentStatuses,
    employeeFunctions,
    emp_category,
    employees,
    accrualThresholds,
    carryoverPeriods,
    annual_refresh_cycle,
    leave_claim_rules,
  } = useSelector((state) => state.authReducer.setup_wizard_details);
  const labor_setting_essentials = useSelector(
    (state) => state.authReducer.labor_setting_essentials
  );
  const initialForm = {
    id: "",
    rule_name: "",
    leave_type_id: "",
    employment_status_id: "",
    employee_function_id: "",
    employee_category_id: "",
    employee_category: "",
    employee_select: [],
    allotment_amount: "",
    accrual_type_id: "",
    birthday_expired: "",
    expiry_months: "",
    accrual_threshold_id: "",
    carryover_period_id: "",
    maximum_carryover_days: 0,
    annual_refresh_cycle: "",
    additional_allotment_full_cycle: 0,
    additional_allotment_full_cycle_days: 0,
    max_allotment_amount: 0,
  };
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [ruleModal, setRuleModal] = useState(false);
  const [templateModal, setTemplateModal] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [empsArr, setEmpsArr] = useState([]);
  const [inputForm, setInputForm] = useState(initialForm);

  const changeInputHandler = (input, val) => {
    if (input == "leave_type_id") {
      dispatch(getAccrualTypes(val));
    }

    // employee all selected option
    if (
      input == "employee_select" &&
      val.length > 0 &&
      val.findIndex((p) => p.value == "all") > -1
    ) {
      val = addEmpToArray(true);
    } else if (
      input == "employee_select" &&
      inputForm?.employee_select?.length > 0 &&
      inputForm?.employee_select.findIndex((p) => p.value == "all") > -1 &&
      val.findIndex((p) => p.value == "all") == -1
    ) {
      val = [];
    }
    // employee all selected option END

    setValue(input, val);
    setInputForm((prevState) => ({ ...prevState, [input]: val }));
  };

  const initialModalState = () => {
    setInputForm(initialForm);
    reset(initialForm);
  };

  const closeModal = useCallback(
    (value) => {
      setRuleModal(value);
      initialModalState();
    },
    [ruleModal]
  );

  const closeTemplateModal = useCallback(
    (value) => {
      setTemplateModal(value);
    },
    [ruleModal]
  );

  const addEmpToArray = (isDisable = false) => {
    let newEmpArr = [];
    Object.keys(employees).map((emp) => {
      const newEmpObj = { value: emp, label: employees[emp] };
      if (emp !== "all" && isDisable) {
        newEmpObj.isDisabled = true;
      }
      newEmpArr.push(newEmpObj);
    });
    return newEmpArr;
  };

  useEffect(() => {
    if (employees && employees !== null) {
      setEmpsArr(addEmpToArray());
    }
  }, [employees]);

  const editRuleHandler = (editRule, isTemplate = false) => {
    if (isTemplate) closeTemplateModal(false);
    closeModal(true);
    const editValues = {
      id: isTemplate ? "" : checkNull(editRule?.id),
      rule_name: isTemplate ? "" : checkNull(editRule?.rule_name),
      leave_type_id: checkNull(editRule?.leave_type_id),
      employment_status_id: checkNull(editRule?.employment_status_id),
      employee_function_id: checkNull(editRule?.employee_function_id),
      employee_category_id: checkNull(editRule?.employee_category_id),
      employee_category: "",
      employee_select: [],
      allotment_amount: checkNull(editRule?.allotment_amount),
      accrual_type_id: checkNull(editRule?.accrual_type_id),
      birthday_expired: checkNull(editRule?.birthday_expired),
      expiry_months: checkNull(editRule?.expiry_months),
      accrual_threshold_id: checkNull(editRule?.accrual_threshold_id),
      carryover_period_id: checkNull(editRule?.carryover_period_id),
      maximum_carryover_days: checkNull(editRule?.maximum_carryover_days, 0),
      annual_refresh_cycle: checkNull(editRule?.annual_refresh_cycle),
      additional_allotment_full_cycle: checkNull(
        editRule?.additional_allotment_full_cycle,
        0
      ),
      additional_allotment_full_cycle_days: checkNull(
        editRule?.additional_allotment_full_cycle_days,
        0
      ),
      max_allotment_amount: checkNull(editRule?.max_allotment_amount, 0),
    };
    if (
      editRule?.employee_select &&
      checkNull(editRule?.employee_select, false)
    ) {
      let newEmpArr = [];
      if (editRule?.employee_select.split(",").length > 0) {
        editRule?.employee_select.split(",").map((emp) => {
          if (employees?.[emp]) {
            newEmpArr.push({ value: emp, label: employees[emp] });
          }
        });
      }
      editValues.employee_select = newEmpArr;
    }
    setInputForm(editValues);
    reset(editValues);
  };

  const submitForm = (data) => {
    data.employee_select = data.employee_select.map((emp) => emp.value);
    console.log("submitForm", data);
    dispatch(saveLaborRule(data, "leave"));
    closeModal(false);
  };

  const templateSubmitHandler = () => {
    if (
      templateId &&
      leave_claim_rules &&
      leave_claim_rules.length > 0 &&
      leave_claim_rules.findIndex((p) => p.id == templateId) > -1
    ) {
      const templateIndex = leave_claim_rules.findIndex(
        (p) => p.id == templateId
      );
      editRuleHandler(leave_claim_rules?.[templateIndex], true);
    } else {
      dispatch(
        showNotificationWithTimeout("Please select valid template!", "error")
      );
    }
  };

  return (
    <>
      <h4 className="sm-hd light mt-5"> Leave/Claim Rules </h4>

      <div class="table-responsive inner-space screen-scroll">
        <table class="table checklist-table remove-space inventory_table">
          <thead>
            <tr>
              <th> Rule Name &amp; Type </th>
              <th>Employment Status</th>
              <th>Function</th>
              <th>Category</th>
              <th>Employee Name</th>
              <th>Allotment Amount (Days)</th>
              <th>Accrual Type</th>
              <th class="action-td">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leave_claim_rules &&
              leave_claim_rules.length > 0 &&
              leave_claim_rules.map((rule) => {
                return (
                  <tr>
                    <td>
                      <b>{rule?.rule_name}</b> <br /> {rule?.leave_type?.name}
                    </td>
                    <td>
                      {rule?.employment_status_id == "all"
                        ? "All"
                        : rule?.employment_status?.name ?? "-"}
                    </td>
                    <td>
                      {" "}
                      {rule?.employee_function_id == "all"
                        ? "All"
                        : rule?.employee_function?.name ?? "-"}{" "}
                    </td>
                    <td>
                      {" "}
                      {rule?.employee_category_id == "all"
                        ? "All"
                        : rule?.employee_category?.name ?? "-"}{" "}
                    </td>
                    <td>
                      {" "}
                      {checkNull(rule?.employee_select_name)
                        ? capitalizeFirstLetter(rule?.employee_select_name)
                        : "-"}{" "}
                    </td>
                    <td> {rule?.allotment_amount} </td>
                    <td> {rule?.accrual_type?.name} </td>
                    <td class="action-td">
                      <div class="d-flex">
                        {rule?.parent_id == 0 && (
                          <>
                            <a
                              className="open_lcr_edit circle_icon"
                              onClick={() => editRuleHandler(rule)}
                            >
                              <i className="iconSprites edit-icon"></i>
                            </a>
                            <a
                              className="deleterulesleave circle_icon red"
                              onClick={() =>
                                openDeleteRuleModal({
                                  id: rule?.id,
                                  rule: "leave",
                                })
                              }
                            >
                              <i className="iconSprites delete-icon"></i>
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="my-3">
        <a
          id=""
          class="btn green-btn-revert add-plus open_lcr "
          onClick={() => closeModal(true)}
        >
          <i class="i-icon"> </i> Add Rule{" "}
        </a>
        <a
          id=""
          onClick={() => closeTemplateModal(true)}
          class="btn bordered-btn open_lcr_template "
        >
          {" "}
          Add From Template{" "}
        </a>
      </div>
      <ModelContainer
        title={`${inputForm?.id !== "" ? "Edit" : "Add"} Leave/Claim Rules`}
        open={ruleModal}
        closeModal={closeModal}
        submitModal={handleSubmit(submitForm)}
        is_loading={false}
      >
        <form>
          {console.log("Errors", errors)}
          {console.log("inputForm", inputForm)}
          <div className="form-list popup-modal">
            <ul className="px-0">
              <li>
                <label className="required"> Rule Name </label>
                <div className="input-group mb10">
                  <input
                    {...register("rule_name")}
                    className="required"
                    placeholder="Rule Name"
                    type="text"
                    onChange={(event) =>
                      changeInputHandler("rule_name", event?.target?.value)
                    }
                    value={inputForm?.rule_name}
                  />
                  <ErrorContainer errors={errors} name="rule_name" />
                </div>
              </li>
              <li>
                <label className="required"> Type </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("leave_type_id")}
                    name="leave_type_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler("leave_type_id", event?.target?.value)
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.leave_type_id}
                  >
                    <option value=""> Select Type </option>
                    {leaveTypes &&
                      leaveTypes !== null > 0 &&
                      Object.keys(leaveTypes).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {leaveTypes?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="leave_type_id" />
                </div>
              </li>
              <li>
                <label className="required"> Employee Status </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("employment_status_id")}
                    name="employment_status_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "employment_status_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.employment_status_id}
                  >
                    <option value=""> Select Employment Status </option>
                    {employmentStatuses &&
                      employmentStatuses !== null > 0 &&
                      Object.keys(employmentStatuses).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {employmentStatuses?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="employment_status_id" />
                </div>
              </li>
              <li>
                <label className="required"> Employee Function </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("employee_function_id")}
                    name="employee_function_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "employee_function_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.employee_function_id}
                  >
                    <option value=""> Select Employee Function </option>
                    {employeeFunctions &&
                      employeeFunctions !== null > 0 &&
                      Object.keys(employeeFunctions).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {employeeFunctions?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="employee_function_id" />
                </div>
              </li>
              <li>
                <label className="required"> Employee Category </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("employee_category_id")}
                    name="employee_category_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "employee_category_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.employee_category_id}
                  >
                    <option value=""> Select Employee Category </option>
                    {emp_category &&
                      emp_category !== null > 0 &&
                      Object.keys(emp_category).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {emp_category?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="employee_category_id" />
                </div>
              </li>
              {inputForm?.employee_category_id == 1 && (
                <li>
                  <label className="required"> Employee Category </label>
                  <div className="input-group mb10">
                    <input
                      {...register("employee_category")}
                      className="required"
                      type="text"
                      onChange={(event) =>
                        changeInputHandler(
                          "employee_category",
                          event?.target?.value
                        )
                      }
                      value={inputForm?.employee_category}
                    />
                    <ErrorContainer errors={errors} name="employee_category" />
                  </div>
                </li>
              )}

              <li>
                <label className="required"> Employee Select </label>
                <div className="px-0">
                  <Select
                    className="w-100"
                    {...register("employee_select")}
                    value={inputForm?.employee_select}
                    onChange={(values) =>
                      changeInputHandler("employee_select", values)
                    }
                    options={empsArr}
                    isMulti
                    isDisabled={inputForm?.id !== ""}
                  />
                  <ErrorContainer errors={errors} name="employee_select" />
                </div>
              </li>
              <li>
                <label className="required"> Allotment Amount(Days) </label>
                <div className="input-group mb10">
                  <input
                    {...register("allotment_amount")}
                    className="required"
                    placeholder="Allotment Amount"
                    type="text"
                    onChange={(event) =>
                      changeInputHandler(
                        "allotment_amount",
                        event?.target?.value
                      )
                    }
                    value={inputForm?.allotment_amount}
                  />
                  <ErrorContainer errors={errors} name="allotment_amount" />
                </div>
              </li>

              <li>
                <label className="required"> Accrual Type </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("accrual_type_id")}
                    name="accrual_type_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "accrual_type_id",
                        event?.target?.value
                      )
                    }
                    value={inputForm?.accrual_type_id}
                  >
                    <option value=""> Select Accrual Type </option>
                    {labor_setting_essentials?.accrual_types &&
                      labor_setting_essentials?.accrual_types !== null &&
                      Object.keys(labor_setting_essentials?.accrual_types).map(
                        (index) => {
                          return (
                            <option value={index} key={index}>
                              {" "}
                              {
                                labor_setting_essentials?.accrual_types?.[index]
                              }{" "}
                            </option>
                          );
                        }
                      )}
                  </select>
                  <ErrorContainer errors={errors} name="accrual_type_id" />
                </div>
              </li>
              {inputForm?.leave_type_id == 7 && (
                <li>
                  <label className="required"> Expired </label>
                  <div className="input-group mb10">
                    <input
                      {...register("birthday_expired")}
                      className="required"
                      placeholder="Expired"
                      type="text"
                      onChange={(event) =>
                        changeInputHandler(
                          "birthday_expired",
                          event?.target?.value
                        )
                      }
                      value={inputForm?.birthday_expired}
                    />
                    <ErrorContainer errors={errors} name="birthday_expired" />
                  </div>
                </li>
              )}
              {inputForm?.leave_type_id == 9 && (
                <li>
                  <label className="required"> Expiry Months </label>
                  <div className="input-group mb10">
                    <input
                      {...register("expiry_months")}
                      className="required"
                      placeholder="Enter expiry months"
                      type="text"
                      onChange={(event) =>
                        changeInputHandler(
                          "expiry_months",
                          event?.target?.value
                        )
                      }
                      value={inputForm?.expiry_months}
                    />
                    <ErrorContainer errors={errors} name="expiry_months" />
                  </div>
                </li>
              )}
              {[
                3, 4, 7, 8, 11, 12, 13, 15, 16, 19, 20, 23, 24, 29, 30, 37, 38,
              ].includes(parseInt(inputForm?.accrual_type_id)) && (
                <li>
                  <label className="required"> Accrual Threshold </label>
                  <div className="input-wrap select-div">
                    <select
                      {...register("accrual_threshold_id")}
                      name="accrual_threshold_id"
                      className="required changing"
                      onChange={(event) =>
                        changeInputHandler(
                          "accrual_threshold_id",
                          event?.target?.value
                        )
                      }
                      value={inputForm?.accrual_threshold_id}
                    >
                      <option value=""> Select Accrual Threshold </option>
                      {accrualThresholds &&
                        accrualThresholds !== null > 0 &&
                        Object.keys(accrualThresholds).map((index) => {
                          return (
                            <option value={index} key={index}>
                              {" "}
                              {accrualThresholds?.[index]}{" "}
                            </option>
                          );
                        })}
                    </select>
                    <ErrorContainer
                      errors={errors}
                      name="accrual_threshold_id"
                    />
                  </div>
                </li>
              )}
              {[1, 2, 3, 4, 5, 6, 8, 11, 14].includes(
                parseInt(inputForm.leave_type_id)
              ) && (
                <li>
                  <label className="required"> Carryover Period </label>
                  <div className="input-wrap select-div">
                    <select
                      {...register("carryover_period_id")}
                      name="carryover_period_id"
                      className="required changing"
                      onChange={(event) =>
                        changeInputHandler(
                          "carryover_period_id",
                          event?.target?.value
                        )
                      }
                      value={inputForm?.carryover_period_id}
                    >
                      <option value=""> Select Carryover Period</option>
                      {carryoverPeriods &&
                        carryoverPeriods !== null > 0 &&
                        Object.keys(carryoverPeriods).map((index) => {
                          return (
                            <option value={index} key={index}>
                              {" "}
                              {carryoverPeriods?.[index]}{" "}
                            </option>
                          );
                        })}
                    </select>
                    <ErrorContainer
                      errors={errors}
                      name="carryover_period_id"
                    />
                  </div>
                </li>
              )}
              {inputForm?.carryover_period_id != 7 &&
                inputForm?.carryover_period_id !== "" && (
                  <li>
                    <label className="required"> Maximum Carryover Days </label>
                    <div className="input-group mb10">
                      <input
                        {...register("maximum_carryover_days")}
                        className="required"
                        type="text"
                        onChange={(event) =>
                          changeInputHandler(
                            "maximum_carryover_days",
                            event?.target?.value
                          )
                        }
                        value={inputForm?.maximum_carryover_days}
                      />
                      <ErrorContainer
                        errors={errors}
                        name="maximum_carryover_days"
                      />
                    </div>
                  </li>
                )}
              <li>
                <label className="required"> Annual Refresh Cycle </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("annual_refresh_cycle")}
                    name="annual_refresh_cycle"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "annual_refresh_cycle",
                        event?.target?.value
                      )
                    }
                    value={inputForm?.annual_refresh_cycle}
                  >
                    <option value=""> Select </option>
                    {annual_refresh_cycle &&
                      annual_refresh_cycle !== null > 0 &&
                      Object.keys(annual_refresh_cycle).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {annual_refresh_cycle?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="annual_refresh_cycle" />
                </div>
              </li>
              <li>
                <label className="required">
                  {" "}
                  Additional Allotment Each Full Annual Cycle{" "}
                </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("additional_allotment_full_cycle")}
                    name="additional_allotment_full_cycle"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "additional_allotment_full_cycle",
                        event?.target?.value
                      )
                    }
                    value={inputForm?.additional_allotment_full_cycle}
                  >
                    <option value=""> Select </option>
                    <option value="yes"> Yes </option>
                    <option value="no"> No </option>
                  </select>
                  <ErrorContainer
                    errors={errors}
                    name="additional_allotment_full_cycle"
                  />
                </div>
              </li>
              {inputForm?.additional_allotment_full_cycle == "yes" && (
                <>
                  <li>
                    <label className="required">
                      {" "}
                      Additional Allotment Amount Each Full Cycle{" "}
                    </label>
                    <div className="input-group mb10">
                      <input
                        {...register("additional_allotment_full_cycle_days")}
                        className="required"
                        type="text"
                        onChange={(event) =>
                          changeInputHandler(
                            "additional_allotment_full_cycle_days",
                            event?.target?.value
                          )
                        }
                        value={inputForm?.additional_allotment_full_cycle_days}
                      />
                      <ErrorContainer
                        errors={errors}
                        name="additional_allotment_full_cycle_days"
                      />
                    </div>
                  </li>
                  <li>
                    <label className="required">
                      {" "}
                      Maximum Allotment Amount{" "}
                    </label>
                    <div className="input-group mb10">
                      <input
                        {...register("max_allotment_amount")}
                        className="required"
                        type="text"
                        onChange={(event) =>
                          changeInputHandler(
                            "max_allotment_amount",
                            event?.target?.value
                          )
                        }
                        value={inputForm?.max_allotment_amount}
                      />
                      <ErrorContainer
                        errors={errors}
                        name="max_allotment_amount"
                      />
                    </div>
                  </li>
                </>
              )}
            </ul>
          </div>
        </form>
      </ModelContainer>
      {/* From Template section */}
      <ModelContainer
        title={`"Add Leave/Claim Rules`}
        open={templateModal}
        closeModal={closeTemplateModal}
        submitModal={templateSubmitHandler}
        is_loading={false}
      >
        <form>
          <div className="form-list popup-modal">
            <ul className="px-0">
              <li>
                <label className="required"> Select Templates </label>
                <div className="input-wrap select-div">
                  <select
                    name="template_id"
                    className="required changing"
                    onChange={(event) => setTemplateId(event?.target?.value)}
                    value={templateId}
                  >
                    <option value=""> Select </option>
                    {leave_claim_rules &&
                      leave_claim_rules.length > 0 &&
                      leave_claim_rules.map((leave) => {
                        return (
                          <option value={leave?.id} key={leave?.id}>
                            {leave?.rule_name}
                          </option>
                        );
                      })}
                  </select>
                </div>
              </li>
            </ul>
          </div>
        </form>
      </ModelContainer>
      {/* From Template section END */}
    </>
  );
};
export default LeaveRule;
